import {
  Clock,
  Globe,
  MoveLeft,
  MoveRight,
  Notebook,
  ShieldAlert,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { authFetch } from "../../../api";
import { useLocation, useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";

const quarters = [
  { id: 1, title: "Quarter 1", subtitle: "April - June" },
  { id: 2, title: "Quarter 2", subtitle: "Jul - Sep" },
  { id: 3, title: "Quarter 3", subtitle: "Oct - Dec" },
  { id: 4, title: "Quarter 4", subtitle: "Jan - Mar" },
];

export default function SAP_AutomatedTimeline() {
  const baseFileUrl = process.env.REACT_APP_API_ENDPOINT.replace(
    "api/student/",
    "",
  );
  const userId = localStorage.getItem("userId");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [activeClass, setActiveClass] = useState("");
  const [activeQuarter, setActiveQuarter] = useState(1);
  const [activities, setActivities] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [enrolledClass, setEnrolledClass] = useState(null);
  const location = useLocation();
  const { classID, stream } = location.state || {};
  console.log("CLASS Id", classID);
  const navigate = useNavigate();
  const currentClass = Number(classID);
  const nextClass = currentClass + 1;
  const fileInputRef = useRef(null);
  const activeIndexRef = useRef(null);

  const handleUploadClick = (index) => {
    activeIndexRef.current = index;
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const task = filteredActivities[activeIndexRef.current];
    if (!task) return;

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("task_id", task.id);
    formData.append("file", file);

    try {
      const response = await authFetch("timeline/upload-student-tasks", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setUploadStatus("success");
        fetchData();
      } else {
        setUploadStatus("error");
        alert("Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadStatus("error");
    }

    e.target.value = "";
  };

  const fetchData = async () => {
    try {
      const response = await authFetch(
        "timeline/show-customized-data-by-enrollment",
        {
          method: "POST",
          body: JSON.stringify({
            student_enrolled_for: Number(classID),
            stream: Number(stream),
            user_id: userId,
          }),
          headers: { "Content-Type": "application/json" },
        },
      );

      const result = await response.json();
      if (result?.success) {
        setActivities(result.data);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const checkClass = async () => {
    try {
      const res = await authFetch("timeline/show-student-enrolled-class", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      setEnrolledClass(result.data.student_enrolled_for);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (activities.length > 0) {
      setActiveClass(activities[0].class_name);
    }
  }, [activities]);

  useEffect(() => {
    fetchData();
    checkClass();
  }, []);

  const filteredActivities = activities.filter(
    (item) => item.class_name === activeClass && item.quarter === activeQuarter,
  );

  useEffect(() => {
    const match = activities.find((a) => a.quarter === activeQuarter);

    if (match) {
      setActiveClass(match.class_name);
    }
  }, [activeQuarter, activities]);

  const handleMoveQuarter = async (task) => {
    if (!task) return;

    // prevent moving from Q4
    if (task.quarter === 4) {
      alert("Task is already in the last quarter");
      return;
    }

    const nextQuarter = task.quarter + 1;

    try {
      const res = await authFetch("timeline/shift-task-to-next-quarter", {
        method: "POST",
        body: JSON.stringify({
          user_id: userId,
          task_id: task.id,
          quarter: nextQuarter,
        }),
        headers: { "Content-Type": "application/json" },
      });

      const result = await res.json();

      if (result.success) {
        fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (uploadStatus === "success") {
      const timer = setTimeout(() => {
        setUploadStatus(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [uploadStatus]);

  const CheckUploadStatus = async () => {
    try {
      const response = await authFetch("timeline/check-quarter-upload-status", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          class_id: currentClass,
          stream: stream,
          quarter: activeQuarter,
        }),
      });

      const data = await response.json();

      if (data?.success) {
        if (data.data) {
          //  Allow moving to next class
          localStorage.setItem("completedClass", currentClass);
          navigate("/sap/select-timeline");
        } else {
          //  Show snackbar
          setSnackbar({
            open: true,
            message: "Upload at least 4 assignments per quarter",
            severity: "error",
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="w-full font-inter">
      <UploadResultModal
        status={uploadStatus}
        onClose={() => setUploadStatus(null)}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <div className="w-full max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.mp4"
          className="hidden"
          onChange={handleFileChange}
        />

        <div className="relative font-inter bg-gradient-to-r from-[#5F6EB4] to-[#C059C0] rounded-xl p-4 sm:p-6 lg:p-8 text-white">
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
            <button
              onClick={() => navigate("/sap/task-progress")}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm rounded-lg bg-white/20 hover:bg-white/30 transition"
            >
              View Your Timeline
            </button>
          </div>

          {/* Header */}
          <h1 className="text-2xl font-bold">
            {`Your Class ${currentClass + 8} Learning Journey Begins`}
          </h1>

          <p className="mt-2 text-sm opacity-90">
            We’ve mapped out your quarter-wise journey to help you explore,
            learn, and shine throughout the year.
          </p>

          {/* Class Tabs */}
          <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
            {[1, 2, 3, 4]
              .filter((num) => num >= enrolledClass)
              .map((num) => {
                // const isLocked = num !== currentClass;
                const isActive = num === currentClass;

                return (
                  <button
                    key={num}
                    className={`relative px-4 py-2 rounded-xl text-sm font-semibold
                ${isActive ? "bg-white text-black" : "bg-white/50"}
              `}
                    // disabled={isLocked}
                  >
                    Class {num + 8}
                    {/* {isLocked && (
                    <img
                      src="/timeline/lock.png"
                      alt="locked"
                      className="absolute -top-1 -right-1 w-4"
                    />
                  )} */}
                  </button>
                );
              })}
          </div>
        </div>

        {/* Quarter Tabs */}
        <div className="mt-6">
          <div className="flex flex-wrap sm:flex-nowrap rounded-xl bg-white border overflow-hidden">
            {quarters.map((q, index) => (
              <button
                key={q.id}
                onClick={() => setActiveQuarter(q.id)}
                className={`relative w-1/2 sm:flex-1 px-4 sm:px-6 py-3 sm:py-4 ${
                  index !== quarters.length - 1
                    ? "border-r border-gray-200"
                    : ""
                }`}
              >
                <p className="font-semibold text-gray-900 mb-1">{q.title}</p>
                <p className="text-sm text-gray-500">{q.subtitle}</p>

                {activeQuarter === q.id && (
                  <span className="absolute bottom-0 left-6 right-6 h-[4px] rounded-full bg-[#97319E]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-6 border rounded-xl p-4 sm:p-6 lg:p-8 bg-white">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
            <div>
              <h3 className="font-bold text-xl">
                Quarter {activeQuarter}:{" "}
                {quarters.find((q) => q.id === activeQuarter)?.subtitle}
              </h3>
              <p className="text-sm text-gray-600 mt-2">
                Begin your foundational discovery journey by exploring various
                interests & joining activities.
              </p>
            </div>

            <span className="px-4 py-2 bg-[#309D7E] text-white text-xs rounded-full">
              {activities.filter((a) => a.type === "completed").length}/4
              Assignment Completed
            </span>
          </div>

          {/* Upload Info */}
          <div className="mt-6 bg-[#EBF3FF] flex items-center gap-2 border rounded-lg p-2 text-sm text-[#163E81]">
            <ShieldAlert />
            Upload your certificate or photo to complete the assignment. Only
            JPG, PDF or MP4 files up to 5MB are allowed.
          </div>

          {/* Activities */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredActivities.map((item, index) => (
              <div className="relative rounded-2xl p-6 bg-gradient-to-r from-[#FFFDF9] to-[#FFF3E0] border border-[#F3E6CF]">
                {item.uploaded_link ? (
                  /* ================= UPLOADED CARD ================= */
                  <>
                    {/* Header */}
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-gray-800 text-lg">
                        {item.task_title}
                      </h4>
                      <a
                        href={`${baseFileUrl}${item.uploaded_link}`}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className="flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl bg-[#9D0485] text-white shadow hover:bg-[#F6EDFA] hover:text-[#9D0485] hover:border-[#9D0485]"
                      >
                        Download
                        <Upload size={16} />
                      </a>
                    </div>

                    {/* File Info */}
                    <div className="flex items-start gap-4 mt-4">
                      <div className="w-12 h-12 bg-white rounded-lg shadow flex items-center justify-center">
                        <img src="/pdf.png" alt="pdf" className="w-7" />
                      </div>

                      <div>
                        <p className="font-semibold text-gray-800">
                          {item.uploaded_link
                            ? item.uploaded_link
                                .split("/")
                                .pop()
                                .split("-")
                                .slice(1)
                                .join("-")
                            : "Uploaded File"}
                        </p>

                        <p className="text-sm text-gray-500 mt-1">
                          Uploaded Successfully
                        </p>

                        <div className="flex items-center gap-6 mt-3 text-sm">
                          <span className="flex items-center gap-2 text-green-600 font-medium">
                            <ShieldCheck size={16} />
                            Verified
                          </span>

                          <button
                            onClick={() => handleUploadClick(index)}
                            className="text-[#6A1B9A] text-sm font-bold hover:underline"
                          >
                            Replace File ↻
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* ================= NORMAL CARD ================= */
                  <>
                    <div className="flex justify-between items-start">
                      <h4 className="font-bold text-lg text-[#0E101A]">
                        {item.task_title}
                      </h4>

                      <button
                        onClick={() => handleUploadClick(index)}
                        className="flex items-center gap-2 px-4 py-1.5 text-sm font-bold rounded-xl border border-[#9D0485] text-white bg-[#9D0485] hover:bg-[#F6EDFA] hover:text-[#9D0485]"
                      >
                        Upload
                        <Upload size={16} />
                      </button>
                    </div>

                    <p className="mt-2 text-sm text-[#5F6368]">
                      {item.task_description?.slice(0, 80)}...
                      <button
                        onClick={() => {
                          setSelectedActivity(item);
                          setOpenModal(true);
                        }}
                        className="ml-1 text-[#6A1B9A] text-sm hover:underline"
                      >
                        View More
                      </button>
                    </p>

                    <div className="mt-6">
                      <button
                        onClick={() => handleMoveQuarter(item)}
                        className="px-5 py-1.5 rounded-xl border font-semibold text-sm border-[#C47A00] text-[#C47A00] bg-white hover:bg-[#FFF6E6]"
                      >
                        Move to Quarter {item.quarter + 1} ›
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          {/* Footer Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row justify-between gap-4">
            <button
              onClick={() => {
                navigate("/sap/select-timeline");
              }}
              className="w-full sm:w-auto px-8 py-2 border rounded-xl text-sm font-semibold text-gray-700"
            >
              Back
            </button>

            {nextClass <= 4 && (
              <button
                onClick={() => {
                  // localStorage.setItem("completedClass", currentClass);
                  // navigate("/sap/select-timeline");
                  CheckUploadStatus();
                }}
                className="px-14 py-3 rounded-xl font-semibold text-sm text-white shadow bg-[#572575] hover:bg-purple-800"
              >
                Proceed To Class {nextClass + 8}
              </button>
            )}
          </div>
        </div>
      </div>
      <ActivityModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        data={selectedActivity}
      />
    </div>
  );
}

const ActivityModal = ({ open, onClose, data }) => {
  const [index, setIndex] = useState(0);

  if (!open || !data) return null;

  const subActivities = data.subActivities || [];
  const currentSub = subActivities[index];

  const handleNext = () => {
    setIndex((prev) => (prev === subActivities.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setIndex((prev) => (prev === 0 ? subActivities.length - 1 : prev - 1));
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-sm font-inter">
      <div className="relative w-[92%] max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* HEADER */}
        <div className="w-full px-6 py-4 border-b flex items-center justify-between bg-white sticky top-0 z-10">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            {data.task_title}
          </h2>

          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide">
          {/* DESCRIPTION */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {data.task_description}
            </p>
          </div>

          {/* COURSE CARD */}
          {currentSub && (
            <div className="relative bg-[#F9F4EE] rounded-2xl p-6 border">
              {/* PREV BUTTON */}
              {subActivities.length > 1 && (
                <button
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 
                 w-10 h-10 rounded-full bg-gray-200 shadow-md 
                 hover:scale-105 transition flex items-center justify-center"
                >
                  <MoveLeft size={18} />
                </button>
              )}

              {/* NEXT BUTTON */}
              {subActivities.length > 1 && (
                <button
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 
                 w-10 h-10 rounded-full bg-gray-200 shadow-md 
                 hover:scale-105 transition flex items-center justify-center"
                >
                  <MoveRight size={18} />
                </button>
              )}
              <div className="flex bg-white rounded-2xl shadow-md overflow-hidden min-h-[260px]">
                {/* LEFT IMAGE FULL HEIGHT */}
                <div className="w-56 ">
                  <img
                    src="/subActivity.png"
                    alt="sub-activity"
                    className=" inset-0 w-full h-full object-cover"
                  />
                </div>

                {/* RIGHT CONTENT */}
                <div className="flex-1 p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-gray-900 text-lg">
                        {currentSub.sub_activity_name}
                      </h4>
                    </div>

                    <p className="text-sm text-gray-600 mt-3">
                      {currentSub.sub_activity_description}
                    </p>

                    {/* Link */}
                    <a
                      href={`https://${currentSub.link}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-blue-600 block mt-3 underline"
                    >
                      {currentSub.link}
                    </a>

                    {/* Meta Info */}
                    <div className="mt-5 grid grid-cols-2 gap-y-3 gap-x-6 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Clock size={16} />
                        <span>Duration - {currentSub.duration} hours</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <ShieldCheck size={16} />
                        <span>Certificate - {data.certificate_name}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Notebook size={16} />
                        <span>Assessment - {currentSub.no_of_assessment}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Globe size={16} />
                        <span>Language - {currentSub.project_language}</span>
                      </div>
                    </div>

                    {/* Tools */}
                    <div className="mt-4 text-sm">
                      <span className="font-medium">Tools Required: </span>
                      {currentSub.tools_required}
                    </div>
                  </div>
                </div>
              </div>

              {/* DOTS */}
              {subActivities.length > 1 && (
                <div className="flex justify-center gap-2 mt-5">
                  {subActivities.map((_, i) => (
                    <span
                      key={i}
                      className={`transition-all duration-300 ${
                        i === index
                          ? "w-6 h-1.5 bg-orange-400"
                          : "w-2 h-2 bg-gray-300"
                      } rounded-full`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const UploadResultModal = ({ status, onClose }) => {
  if (!status) return null;

  const isSuccess = status === "success";

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 ">
      <div className="relative w-[90%] max-w-lg bg-white rounded-3xl p-10 text-center shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
        >
          ✕
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          {isSuccess ? (
            <div className="w-24 h-24 rounded-full  flex items-center justify-center">
              <img
                src="/success.gif"
                alt="success"
                size={54}
                className="text-white"
              />
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full  flex items-center justify-center">
              <img src="/warning.png" size={54} className="text-white" />
            </div>
          )}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {isSuccess ? "Upload Successfully!" : "Upload Failed!"}
        </h2>

        {/* Message */}
        <p className="text-gray-600 mb-8">
          {isSuccess
            ? "Your proof of document has been uploaded successfully."
            : "Please try again or check your document size and internet connection."}
        </p>

        {/* Button */}
        {!isSuccess && (
          <button
            onClick={onClose}
            className="px-8 py-3 bg-[#572575] text-white rounded-xl shadow hover:bg-purple-800"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};
